import React, { useEffect, useState } from 'react';
import IsoDatePicker from './IsoDatePicker';
import { acAvailabilityLocal, acQuoteLocal, acNightsBetween } from '../utils/availabilityLocal';

export default function ACInlinePicker({ offering, onPick, bookingRange, acSeasons, acModifiers = [], live, defaultDate = '', hideAdd = false, onDraftChange }) {
  const [start, setStart] = useState(defaultDate || '');
  const [end, setEnd] = useState('');
  const [available, setAvailable] = useState(null);
  const [adding, setAdding] = useState(false);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rooms, setRooms] = useState(1); // requested number of rooms
  const [startInclude, setStartInclude] = useState([]);
  const seasonDays = React.useMemo(() => {
    if (!bookingRange?.start_date || !bookingRange?.end_date) return [];
    const rows = (acSeasons || []).filter(s => s.room_type_id === offering.id);
    const set = new Set();
    const clipStart = new Date(bookingRange.start_date);
    const clipEnd = new Date(bookingRange.end_date);
    rows.forEach(s => {
      const a = new Date(s.start_date);
      const b = new Date(s.end_date);
      const from = a < clipStart ? clipStart : a;
      const to = b > clipEnd ? clipEnd : b;
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        set.add(d.toISOString().slice(0, 10));
      }
    });
    return Array.from(set).sort();
  }, [acSeasons, offering.id, bookingRange?.start_date, bookingRange?.end_date]);

  // Compute which start dates (check-in) are actually available for the requested rooms
  useEffect(() => {
    if (!bookingRange?.start_date || !bookingRange?.end_date) { setStartInclude([]); return; }
    const out = [];
    const horizonStart = new Date(bookingRange.start_date);
    const horizonEnd = new Date(bookingRange.end_date);
    const seasonSet = new Set(seasonDays);
    for (let d = new Date(horizonStart); d < horizonEnd; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0, 10);
      if (!seasonSet.has(iso)) continue; // must be priced
      const next = new Date(d); next.setDate(next.getDate() + 1); // minimum 1 night
      const avail = acAvailabilityLocal({
        roomTypeId: offering.id,
        startDate: iso,
        endDate: next.toISOString().slice(0, 10),
        quantity: offering.quantity,
        seasons: acSeasons,
        live,
      });
      if ((avail.available || 0) >= Math.max(1, Number(rooms || 1))) out.push(iso);
    }
    setStartInclude(out);
    // if current start is no longer valid, clear it and downstream end/date-dependent state
    if (start && !out.includes(start)) {
      setStart('');
      setEnd('');
    }
  }, [bookingRange?.start_date, bookingRange?.end_date, offering.id, offering.quantity, JSON.stringify(live), acSeasons?.length, rooms, seasonDays.join('|')]);

  async function check() {
    if (!start || !end) return;
    setLoading(true);
    try {
      const avail = acAvailabilityLocal({
        roomTypeId: offering.id,
        startDate: start,
        endDate: end,
        quantity: offering.quantity,
        seasons: acSeasons,
        live,
      });
      setAvailable(avail.available ?? 0);
      const q = acQuoteLocal({
        roomTypeId: offering.id,
        startDate: start,
        endDate: end,
        seasons: acSeasons,
        modifiers: acModifiers,
        remainingByNight: avail.remainingByNight || {},
      });
      setQuote(q);
    } finally {
      setLoading(false);
    }
  }

  // NEW: if a start/end is selected, re-check when live availability changes
  useEffect(() => {
    if (!start || !end) return;
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(live)]);

  // useEffect(() => {
  //   if (!start) { setEndInclude([]); return; }
  //   const out = [];
  //   let d = new Date(start);
  //   d.setDate(d.getDate() + 1);
  //   while (seasonDays.includes(d.toISOString().slice(0, 10))) {
  //     out.push(d.toISOString().slice(0, 10));
  //     d.setDate(d.getDate() + 1);
  //   }
  //   setEndInclude(out);
  // }, [start, seasonDays.join('|')]);

  const [endInclude, setEndInclude] = useState([]);
  useEffect(() => {
    if (!start) { setEndInclude([]); return; }
    const out = [];
    let d = new Date(start);
    d.setDate(d.getDate() + 1);
    const horizonEnd = bookingRange?.end_date ? new Date(bookingRange.end_date) : null;
    for (let i = 0; i < 365; i++) {
      const cand = d.toISOString().slice(0, 10);
      if (horizonEnd && d > horizonEnd) break;
      const r = acAvailabilityLocal({
        roomTypeId: offering.id,
        startDate: start,
        endDate: cand,
        quantity: offering.quantity,
        seasons: acSeasons,
        live,
      });
      if ((r.available || 0) >= Math.max(1, Number(rooms || 1))) out.push(cand);
      else break;
      d.setDate(d.getDate() + 1);
    }
    setEndInclude(out);
    if (end && !out.includes(end)) setEnd('');
  }, [start, JSON.stringify(live), offering.id, offering.quantity, acSeasons?.length, bookingRange?.end_date, rooms]);

  // Auto-check availability and emit draft when inputs are sufficient (mirrors Add enablement)
  useEffect(() => {
    let cancelled = false;
    async function autoCheckAndEmit() {
      if (!start || !end) {
        if (typeof onDraftChange === 'function') try { onDraftChange(offering, null); } catch (e) {}
        return;
      }
      // compute availability and quote in background (same as check())
      const avail = acAvailabilityLocal({
        roomTypeId: offering.id,
        startDate: start,
        endDate: end,
        quantity: offering.quantity,
        seasons: acSeasons,
        live,
      });
      const q = acQuoteLocal({
        roomTypeId: offering.id,
        startDate: start,
        endDate: end,
        seasons: acSeasons,
        modifiers: acModifiers,
        remainingByNight: avail.remainingByNight || {},
      });
      if (!cancelled) {
        setAvailable(avail.available ?? 0);
        setQuote(q);
      }
      if (typeof onDraftChange === 'function') {
        const ok = (avail.available || 0) >= Math.max(1, Number(rooms || 1));
        const draft = ok ? { ac_start_date: start, ac_end_date: end, party_size: Math.max(1, Number(rooms || 1)) } : null;
        try { onDraftChange(offering, draft); } catch (e) {}
      }
    }
    autoCheckAndEmit();
    return () => { cancelled = true; };
  }, [start, end, offering, offering?.id, offering?.quantity, JSON.stringify(live), acSeasons?.length, acModifiers?.length, onDraftChange, rooms]);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div>
          <label className="block text-xs text-gray-600">From</label>
          <IsoDatePicker
            value={start}
            onChange={setStart}
            minDate={bookingRange?.start_date}
            maxDate={bookingRange?.end_date}
            includeDates={startInclude}
            includeDatesStrict
            isClearable
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600">To</label>
          <IsoDatePicker
            value={end}
            onChange={setEnd}
            minDate={start || bookingRange?.start_date}
            maxDate={bookingRange?.end_date}
            includeDates={endInclude}
            isClearable
          />
        </div>
        <div>
          <label className="block text-xs text-gray-600">Rooms</label>
          <input
            type="number"
            min={1}
            max={Math.max(1, Number(offering?.quantity || 1))}
            value={rooms}
            onChange={e => setRooms(Math.min(Math.max(1, Number(e.target.value || 1)), Math.max(1, Number(offering?.quantity || 1))))}
            className="border rounded px-2 py-1 w-24"
          />
        </div>
          <button onClick={check} className="px-3 py-2 rounded bg-black text-white text-sm">Check</button>
      </div>

      {loading && <div className="text-xs text-gray-500">Checking…</div>}
      {available != null && !loading && (
        <div className="flex items-center justify-between">
          <div className="text-xs text-gray-600">Rooms left: {available}</div>
          {!hideAdd && (
            <button
              disabled={!start || !end || (available || 0) < Math.max(1, Number(rooms || 1)) || adding}
              onClick={async () => {
                setAdding(true);
                try {
                  await onPick(offering, {
                    slot_start: null,
                    slot_end: null,
                    party: Math.max(1, Number(rooms || 1)),
                    price: (quote?.total != null ? Number(quote.total) * Math.max(1, Number(rooms || 1)) : Number(offering.price || 0) * Math.max(1, Number(rooms || 1))),
                    guests_json: { ac_start_date: start, ac_end_date: end, party_size: Math.max(1, Number(rooms || 1)) }
                  });
                } finally { setAdding(false); }
              }}
              className="px-3 py-2 text-sm rounded bg-black text-white disabled:opacity-40"
            >
              {(available || 0) >= Math.max(1, Number(rooms || 1)) ? (adding ? 'Adding…' : `Add ${Math.max(1, Number(rooms || 1))} room${Math.max(1, Number(rooms || 1))>1?'s':''} • €${(quote?.total != null
                ? (Number(quote.total) * Math.max(1, Number(rooms || 1)))
                : (Number(offering.price || 0) * Math.max(1, Number(rooms || 1))))
                .toFixed(2)}`) : 'No rooms'}
            </button>
          )}
        </div>
      )}
      {!loading && quote?.nights?.length ? (
        <div className="text-[11px] text-gray-500 mt-1">
          {acNightsBetween(start, end).length} night(s) • Total: €{(Number(quote.total || 0) * Math.max(1, Number(rooms || 1))).toFixed(2)}
        </div>
      ) : null}
    </div>
  );
}
